import { Controller } from "@hotwired/stimulus"
import { debounce } from "../utils"
import { rrFetch } from "../utils/rr_fetch"

export default class DepositWalletController extends Controller {
    static targets = ["topToken", "selectedTokenIcon", "searchTokenInput", "amountInput", "depositButton", "loadingSpinner", "depositButtonText", "processingMessage", "changeDepositOptionsButton"]

    static values = {
        selectedTokenId: String,
        selectedNetwork: String,
        amountNeeded: String,
        csrfToken: String,
        idemKey: String,
        withdrawFromCrypto: Boolean,
    }

    connect() {
        this.setupEventListeners()
        this.debouncedTokenSearch = debounce(this.searchTokens.bind(this), 300)
        this.initializeTokenSelection()
    }

    setupEventListeners() {
        this.searchTokenInputTarget.addEventListener("focus", (e) => {
            e.currentTarget.select()
        })

        if (this.hasAmountInputTarget) {
            this.amountInputTarget.addEventListener("focus", (e) => {
                e.currentTarget.select()
            })
        }

        this.searchTokenInputTarget.addEventListener("input", this.handleTokenInputChange.bind(this))
    }

    initializeTokenSelection() {
        if (this.hasSelectedTokenIdValue && this.selectedTokenIdValue) {
            const tokenElement = this.topTokenTargets.find(token => token.dataset.tokenId === this.selectedTokenIdValue)
            if (tokenElement) {
                this.selectTop3Token(tokenElement)
            } else {
                this.handleTokenNotInTop3()
            }
        } else if (this.topTokenTargets.length > 0) {
            this.selectTop3Token(this.topTokenTargets[0])
        }
    }

    handleTokenNotInTop3() {
        const payloadData = this.element.dataset.payload
        if (payloadData) {
            try {
                this.setTokenFromPayload(payloadData)
            } catch (e) {
                console.error("Failed to parse payload:", e)
                this.fallbackToFirstToken()
            }
        } else {
            this.fallbackToFirstToken()
        }
    }

    setTokenFromPayload(payloadData) {
        const payload = JSON.parse(payloadData)
        const tokenName = payload.tokenName || payload.tokenSymbol || this.selectedTokenIdValue
        const tokenIcon = payload.tokenIcon || "https://api.houdiniswap.com/assets/default.png"

        if (this.hasSelectedTokenIconTarget) {
            this.selectedTokenIconTarget.src = tokenIcon
        }
        if (this.hasSearchTokenInputTarget) {
            this.searchTokenInputTarget.value = tokenName
        }

        const hiddenInput = document.getElementById('selected-token-id')
        if (hiddenInput) {
            hiddenInput.value = this.selectedTokenIdValue
        }
    }

    fallbackToFirstToken() {
        if (this.topTokenTargets.length > 0) {
            this.selectTop3Token(this.topTokenTargets[0])
        }
    }

    handleSelectTop3Tokens(event) {
        const clickedToken = event.currentTarget
        this.selectTop3Token(clickedToken)
    }

    async selectTop3Token(tokenElement) {
        // Remove selection from all tokens
        console.log("selectTop3Token", tokenElement);
        this.topTokenTargets.forEach(token => {
            token.classList.remove('selected-token')
        })

        // Add selection to clicked token
        tokenElement.classList.add('selected-token')

        // Hide the entire search token list
        const tokenList = document.getElementById('wallet-houdini-tokens-list')
        if (tokenList) {
            tokenList.style.display = 'none'
        }

        // Update search icon and input value to show selected token's icon and name
        const tokenIcon = tokenElement.dataset.tokenIcon
        const tokenName = tokenElement.dataset.tokenName
        const tokenId = tokenElement.dataset.tokenId
        const network = tokenElement.dataset.network

        // Store selected token data
        this.selectedTokenIdValue = tokenId
        this.selectedNetworkValue = network

        if (tokenIcon && this.hasSelectedTokenIconTarget) {
            this.selectedTokenIconTarget.src = tokenIcon
            this.searchTokenInputTarget.value = tokenName
        }

    }

    handleTokenInputChange(event) {
        const searchValue = event.target.value.trim()

        // Only search if there's actually something to search for
        if (this.topTokenTargets.length >= 1) {
            // Only show default icon when input is empty or cleared
            // Keep selected token icon if user is just editing the search text
            if (searchValue === "" && this.hasSelectedTokenIconTarget) {
                this.selectedTokenIconTarget.src = "https://api.houdiniswap.com/assets/default.png";
            }
            this.debouncedTokenSearch(searchValue)
        }
    }

    async searchTokens(searchValue) {
        try {
            const amountNeeded = this.searchTokenInputTarget.dataset.amountNeeded || "20"
            const csrfToken = this.csrfTokenValue

            const response = await rrFetch("/deposit/crypto/search-tokens", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": csrfToken,
                },
                body: JSON.stringify({
                    search: searchValue,
                    amountNeeded: amountNeeded
                })
            })

            const responseData = await response.json();

            if (responseData.type === "html") {
                Turbo.renderStreamMessage(responseData.data);
            } else {
                console.error("error", responseData.error);
                this.showError(responseData.error);
            }
        } catch (error) {
            console.error("Error searching tokens:", error)
            this.showError("Error searching tokens");
        }
    }

    showError(errorMessage) {
        const errorDiv = document.getElementById("error-message");
        if (errorDiv) {
            errorDiv.textContent = errorMessage;
            errorDiv.classList.remove("hidden");
        }

        setTimeout(() => {
            this.hideError();
        }, 5000);
    }

    handleSelectSearchToken(event) {
        const clickedToken = event.currentTarget
        const tokenIcon = clickedToken.dataset.tokenIcon
        const tokenName = clickedToken.dataset.tokenName
        const tokenId = clickedToken.dataset.tokenId
        const network = clickedToken.dataset.network

        // Store selected token data
        this.selectedTokenIdValue = tokenId
        this.selectedNetworkValue = network

        // Update search input to show selected token's name
        if (tokenName && this.hasSearchTokenInputTarget) {
            this.searchTokenInputTarget.value = tokenName + " (" + network + ")"
        }

        // Update search icon to show selected token's icon
        if (tokenIcon && this.hasSelectedTokenIconTarget) {
            this.selectedTokenIconTarget.src = tokenIcon
        }

        const hiddenInput = document.getElementById('selected-token-id')
        if (hiddenInput) {
            hiddenInput.value = tokenId
        }

        // Clear top 3 token styles
        this.clearTop3TokenStyles();

        this.selectListToken(clickedToken.dataset.tokenId)
    }

    async selectListToken(tokenId) {
        try {
            const csrfToken = this.csrfTokenValue

            const response = await rrFetch(
                "/deposit/crypto/select-token",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-Token": csrfToken,
                    },
                    body: JSON.stringify({
                        tokenId: tokenId,
                    }),
                }
            );

            const responseData = await response.json();

            if (responseData.type === "html") {
                Turbo.renderStreamMessage(responseData.data);
            } else {
                console.error("error", responseData.error);
            }
        } catch (error) {
            console.error("error", error);
        }
    }

    clearTop3TokenStyles() {
        this.topTokenTargets.forEach(token => {
            token.classList.remove('selected-token')
        })
    }

    handleAmountInput(e) {
        const input = e.currentTarget;
        let value = input.value;

        // Only allow numbers and one decimal point
        value = value.replace(/[^0-9.]/g, '');

        // Ensure only one decimal point
        const parts = value.split('.');
        if (parts.length > 2) {
            value = parts[0] + '.' + parts.slice(1).join('');
        }

        // Update the input value
        input.value = value;
    }

    showAmountError(errorMessage, minimumAmount = null) {
        const errorDiv = document.getElementById("amount-error-message");
        const errorText = document.getElementById("error-text");
        const amountDisplay = this.hasAmountInputTarget ? this.amountInputTarget : null;

        if (errorDiv && errorText) {
            // Set the error message text
            errorText.textContent = errorMessage;
            errorDiv.classList.remove("hidden");

            // Update amount to show the corrected/adjusted amount if provided
            if (minimumAmount && amountDisplay) {
                // Update amount to show the corrected/adjusted amount (no $ prefix)
                amountDisplay.value = minimumAmount.toFixed(2);
                // Store the minimum amount for retry
                this.minimumAmountValue = minimumAmount;

                amountDisplay.dispatchEvent(new Event("input"));

                // Update the deposit button data attribute with the new amount
                if (this.hasDepositButtonTarget) {
                    this.depositButtonTarget.dataset.amountNeeded = minimumAmount;
                }

                // Update hidden input if it exists
                const hiddenAmountInput = document.getElementById('amount-needed');
                if (hiddenAmountInput) {
                    hiddenAmountInput.value = minimumAmount.toFixed(2);
                }
            }

            // Auto-hide error message after 5 seconds
            setTimeout(() => {
                this.hideAmountError();
            }, 5000);
        } else {
            console.error("Error: Could not find errorDiv or errorText elements");
        }
    }

    hideAmountError() {
        const errorDiv = document.getElementById("amount-error-message");
        if (errorDiv) {
            errorDiv.classList.add("hidden");
        }
    }

    lockInputs() {
        this.searchTokenInputTarget.disabled = true;
        if (this.hasAmountInputTarget) {
            this.amountInputTarget.disabled = true;
        }
        this.topTokenTargets.forEach(btn => btn.disabled = true);
    }

    hideChangeDepositOptionsButton() {
        this.changeDepositOptionsButtonTarget.classList.add("hidden");
    }

    showChangeDepositOptionsButton() {
        this.changeDepositOptionsButtonTarget.classList.remove("hidden");
    }

    showLoadingSpinner() {
        this.depositButtonTarget.disabled = true;
        this.searchTokenInputTarget.disabled = true;
        this.loadingSpinnerTarget.classList.remove("hidden");
        this.depositButtonTextTarget.textContent = "Processing...";

        // Show the processing message
        if (!this.withdrawFromCryptoValue) {
            this.processingMessageTarget.classList.remove("hidden");
        }
    }

    hideLoadingSpinner() {
        this.depositButtonTarget.disabled = false;
        this.searchTokenInputTarget.disabled = false;
        if (this.hasAmountInputTarget) {
            this.amountInputTarget.disabled = false;
        }
        this.topTokenTargets.forEach(btn => btn.disabled = false);
        this.loadingSpinnerTarget.classList.add("hidden");
        this.depositButtonTextTarget.textContent = "Deposit";

        // Hide the processing message
        if (!this.withdrawFromCryptoValue) {
            this.processingMessageTarget.classList.add("hidden");
        }
    }

    async handleWalletFindBestRoute(event) {

        const amountNeeded = (this.hasAmountInputTarget ? this.amountInputTarget.value : null) || this.amountNeededValue;
        const selectedTokenId = this.selectedTokenIdValue;
        const csrfToken = this.csrfTokenValue;

        if (!this.withdrawFromCryptoValue && (!amountNeeded || amountNeeded.trim() === "")) {
            console.error("Amount is empty!");
            return;
        }

        this.hideAmountError();

        this.showLoadingSpinner();

        this.lockInputs();

        this.hideChangeDepositOptionsButton();

        try {
            // Determine endpoint based on withdrawFromCrypto value
            const endpoint = this.withdrawFromCryptoValue
                ? "/withdraw/crypto/select-asset"
                : "/deposit/crypto/find-best-route";

            const response = await rrFetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": csrfToken,
                },
                body: JSON.stringify({
                    amount: amountNeeded,
                    houdiniTokenId: selectedTokenId,
                    idemKey: this.idemKeyValue
                }),
            });

            const contentType = response.headers.get("content-type") || "";

            if (contentType.includes("text/vnd.turbo-stream.html")) {
                const html = await response.text();

                Turbo.renderStreamMessage(html);
                this.showChangeDepositOptionsButton();
            } else if (contentType.includes("application/json")) {
                const responseData = await response.json();
                if (responseData.type === "error") {
                    this.showAmountError(responseData.error, responseData.minimumAmount);
                    this.showChangeDepositOptionsButton();
                } else if (responseData.type === "redirect" && responseData.url) {
                    Turbo.visit(responseData.url);
                    return;
                }
            }
        } catch (error) {
            console.error("Error happened during find best route", error);
        } finally {
            this.hideLoadingSpinner();
        }
    }
}